import { useState } from 'react';
import { useSavedLineItemsQuery, setSavedLineItemsQuery } from '~/lib/Queries';
import TextInput from '~/components/TextInput';
import { TrashIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import { businessApiRoute, post } from '~/lib/Api';
import * as Toast from '~/lib/Toast.js';
import useNumericDraft from '~/lib/useNumericDraft';

const SavedLineItemsPanel = () => {
    const { data, isLoading } = useSavedLineItemsQuery();
    const savedLineItems = data?.savedLineItems || [];
    const [editingItem, setEditingItem] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const handleAdd = () => {
        setEditingItem({ id: 0, name: '', description: '', price: 0, quantity: 1 });
    };

    const handleEdit = (item) => {
        setEditingItem({ ...item });
    };

    const handleCancel = () => {
        setEditingItem(null);
    };

    const handleSave = () => {
        if (!editingItem.name || isSaving) return;
        setIsSaving(true);
        post(businessApiRoute(`/invoices/saved-line-items/${editingItem.id || 0}`), {
            name: editingItem.name,
            description: editingItem.description || '',
            price: parseFloat(editingItem.price) || 0,
            quantity: editingItem.quantity,
        }).then(results => {
            setSavedLineItemsQuery(results);
            setEditingItem(null);
        }).catch(() => {
            Toast.error('Failed to save line item');
        }).finally(() => {
            setIsSaving(false);
        });
    };

    const handleDelete = (id) => {
        if (isSaving) return;
        if (!confirm('Are you sure you want to delete this saved line item?\nInvoices which already have this added will not be affected.')) {
            return;
        }

        setIsSaving(true);
        post(businessApiRoute(`/invoices/saved-line-items/${id}/delete`), {}).then(results => {
            setSavedLineItemsQuery(results);
        }).catch(() => {
            Toast.error('Failed to delete line item');
        }).finally(() => {
            setIsSaving(false);
        });
    };

    if (isLoading) {
        return null;
    }

    return (
        <div className="mt-6">
            <p className="text-sm text-gray-500 mb-4">
                Save commonly used line items to quickly add them to invoices.
            </p>

            <div className="panel pl-4">
                {savedLineItems.map((item, idx) => (
                    editingItem?.id === item.id ? (
                        <LineItemForm
                            key={item.id}
                            item={editingItem}
                            setItem={setEditingItem}
                            onSave={handleSave}
                            onCancel={handleCancel}
                            isSaving={isSaving}
                        />
                    ) : (
                        <div key={item.id} className={`${idx > 0 ? 'border-t' : ''} pr-4 py-4 flex items-center justify-between`}>
                            <div className="flex-1 cursor-pointer" onClick={() => handleEdit(item)}>
                                <div className="font-medium">{item.name}</div>
                                {item.description && <div className="text-sm text-gray-500">{item.description}</div>}
                                <div className="text-sm text-gray-600 mt-1">
                                    {item.quantity} × {formatCurrency(item.price)} = {formatCurrency(item.quantity * item.price)}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleEdit(item)}
                                    className="text-gray-500 hover:text-gray-700 p-1"
                                    title="Edit item"
                                >
                                    <PencilIcon className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDelete(item.id)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                    title="Delete item"
                                    disabled={isSaving}
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )
                ))}

                {editingItem && !editingItem.id && (
                    <LineItemForm
                        item={editingItem}
                        setItem={setEditingItem}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        isSaving={isSaving}
                    />
                )}
            </div>
            {!editingItem && (
                <button
                    type="button"
                    onClick={handleAdd}
                    className="btn-light-pill text-sm flex items-center gap-1 mt-4"
                >
                    <PlusIcon className="w-4 h-4" />
                    Add Item
                </button>
            )}
        </div>
    );
};

const PriceInput = ({ value, onChange }) => {
    const [text, handleChange] = useNumericDraft(value, onChange);

    return (
        <TextInput
            label="Price"
            type="number"
            value={text}
            onChange={handleChange}
            min="0"
            step="0.01"
        />
    );
};

const LineItemForm = ({ item, setItem, onSave, onCancel, isSaving }) => {
    return (
        <div className="panel p-4 mb-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                    <TextInput
                        label="Item Name"
                        autoFocus={true}
                        value={item.name}
                        onChange={(value) => setItem({ ...item, name: value })}
                        placeholder="Name of item or service"
                    />
                </div>
                <div className="md:col-span-2">
                    <TextInput
                        label="Description (optional)"
                        value={item.description || ''}
                        onChange={(value) => setItem({ ...item, description: value })}
                        placeholder="Additional details about this item"
                    />
                </div>
                <div>
                    <PriceInput
                        value={item.price}
                        onChange={(value) => setItem({ ...item, price: value })}
                    />
                </div>
                <div>
                    <TextInput
                        label="Quantity"
                        type="number"
                        value={item.quantity?.toString() || '1'}
                        onChange={(value) => setItem({ ...item, quantity: parseFloat(value) || 0 })}
                        min="0"
                        step="1"
                    />
                </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
                <button
                    type="button"
                    onClick={onSave}
                    disabled={isSaving || !item.name}
                    className="btn-save"
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="btn-light-pill-lg"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default SavedLineItemsPanel;

